import { useState } from "react";
import { Shield, Loader2, TrendingUp, AlertTriangle, Building2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { useDeals } from "@/hooks/useDeals";
import { useCalls } from "@/hooks/useCalls";
import { cn } from "@/lib/utils";

interface CompetitorMention {
  competitor: string;
  count: number;
  sentiment: "positive" | "negative" | "neutral";
  context: string;
}

export function AICompetitorAnalysis() {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { chat } = useAIAssistant();
  const { data: deals } = useDeals();
  const { data: calls } = useCalls();

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    
    // Gather all notes and transcripts
    const dealNotes = deals?.map(d => d.title + ": " + (d.metadata as any)?.notes || "").filter(Boolean) || [];
    const callTranscripts = calls?.map(c => c.notes || c.transcript || "").filter(Boolean) || [];
    
    const allContent = [...dealNotes, ...callTranscripts].join("\n\n");
    
    const result = await chat(
      `Analyze the following sales notes and call transcripts for competitor mentions. 
      Identify any competitor names, summarize what's being said about them, and provide strategic insights.
      Format your response with clear sections:
      1. Competitors Mentioned (with frequency)
      2. Key Insights (what prospects are saying about competitors)
      3. Competitive Positioning (how to differentiate)
      4. Action Items (specific recommendations)
      
      Content to analyze:
      ${allContent || "No deal notes or call transcripts available yet. Add notes to your deals or log call transcripts to enable competitor analysis."}`
    );
    
    setAnalysis(result);
    setIsAnalyzing(false);
  };

  // Mock competitor data for display
  const mockCompetitors: CompetitorMention[] = [
    { competitor: "Competitor A", count: 12, sentiment: "neutral", context: "Mentioned in pricing discussions" },
    { competitor: "Competitor B", count: 8, sentiment: "negative", context: "Prospects citing poor support" },
    { competitor: "Competitor C", count: 5, sentiment: "positive", context: "Strong feature set mentions" },
  ];

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Competitor Analysis</h3>
            <p className="text-xs text-muted-foreground">AI-powered insights from deals & calls</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          className="gap-2"
        >
          {isAnalyzing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isAnalyzing ? "Analyzing..." : "Analyze"}
        </Button>
      </div>

      {!analysis && !isAnalyzing && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Competitor mentions detected in your pipeline:
          </div>
          {mockCompetitors.map((comp, i) => (
            <div 
              key={i} 
              className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{comp.competitor}</p>
                  <p className="text-xs text-muted-foreground">{comp.context}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full",
                  comp.sentiment === "positive" && "bg-success/10 text-success",
                  comp.sentiment === "negative" && "bg-destructive/10 text-destructive",
                  comp.sentiment === "neutral" && "bg-muted text-muted-foreground"
                )}>
                  {comp.count} mentions
                </span>
                {comp.sentiment === "positive" ? (
                  <TrendingUp className="h-4 w-4 text-success" />
                ) : comp.sentiment === "negative" ? (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                ) : null}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground text-center pt-2">
            Click "Analyze" for detailed AI insights
          </p>
        </div>
      )}

      {isAnalyzing && (
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Analyzing competitor mentions...</p>
        </div>
      )}

      {analysis && !isAnalyzing && (
        <div className="space-y-3">
          <div className="p-4 rounded-lg bg-muted/30 max-h-80 overflow-y-auto">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-sm">{analysis}</div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setAnalysis(null)}
            className="w-full"
          >
            Show Summary View
          </Button>
        </div>
      )}
    </div>
  );
}
